/**
 * CascadeRespondPage.jsx
 * =======================
 * Public page contractors land on from the HMAC link in their cascade email.
 *
 * Route: /cascade/respond?token=...  (UNAUTHENTICATED — the token is the auth)
 *
 * Behavior:
 *  - GET /api/cascade/respond?token=... → render contractor's bars + status
 *  - If `decision` is in the URL (?decision=confirm or push_back), pre-arm the
 *    matching action and (for confirm) auto-submit on first paint. Push-back
 *    always opens a note field so contractor can explain.
 *  - After response: show a friendly confirmation; second visits are a read-only "already responded" state.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from '@/lib/dateUtils';
import { CheckCircle2, AlertTriangle, Loader2, Calendar, Building2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CascadeRespondPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const initialDecision = params.get('decision'); // 'confirm' | 'push_back' | null

  const [view, setView] = useState(null);  // CascadePublicView
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fetchView = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/cascade/respond`, { params: { token } });
      setView(res.data);
      setSubmitted(['confirmed', 'pushed_back'].includes(res.data.status));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Could not load schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Missing token');
      setLoading(false);
      return;
    }
    fetchView();
  }, [token]); // eslint-disable-line

  // Auto-confirm if URL had decision=confirm and contractor hasn't responded yet
  useEffect(() => {
    if (!view) return;
    if (submitted) return;
    if (initialDecision !== 'confirm') return;
    submit('confirm');
  }, [view, submitted, initialDecision]); // eslint-disable-line

  const submit = async (decision) => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/cascade/respond`, {
        token,
        decision,
        note: decision === 'push_back' ? note.trim() : null,
      });
      setSubmitted(true);
      // re-fetch so we render the post-submit summary
      fetchView();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <div className="bg-white rounded-2xl shadow-xl border border-rose-200 p-6 max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-rose-600" />
            <h1 className="text-lg font-semibold text-slate-900">Hmm, something's off</h1>
          </div>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-500 mt-3">If your link expired, please ask the project manager to resend.</p>
        </div>
      </Centered>
    );
  }

  const c = view?.contractor || {};
  const p = view?.project || {};
  const bars = view?.bars || [];
  const window_ = view?.locked_window || {};
  const status = view?.status;
  const isConfirmed = status === 'confirmed';
  const isPushed = status === 'pushed_back';

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="cascade-respond-page">
          {/* HEADER */}
          <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
            <div className="text-xs font-bold uppercase tracking-widest opacity-90">DrawReady · Schedule confirmation</div>
            <h1 className="text-xl font-bold mt-1">Hi {c.name || c.company || 'there'},</h1>
            <p className="text-sm opacity-95 mt-0.5">
              Please review your dates on <strong>{p.name || 'this project'}</strong>{p.address ? ` (${p.address})` : ''}.
            </p>
          </div>

          {/* STATUS BANNER (post-submit) */}
          {submitted && isConfirmed && (
            <div className="px-6 py-4 bg-emerald-50 border-b border-emerald-200 flex items-center gap-3" data-testid="cascade-status-confirmed">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
              <div>
                <div className="font-semibold text-emerald-900">Confirmed — thank you!</div>
                <div className="text-xs text-emerald-800">The project manager has been notified.</div>
              </div>
            </div>
          )}
          {submitted && isPushed && (
            <div className="px-6 py-4 bg-amber-50 border-b border-amber-200 flex items-center gap-3" data-testid="cascade-status-pushed">
              <AlertTriangle className="w-6 h-6 text-amber-700" />
              <div>
                <div className="font-semibold text-amber-900">Push-back submitted</div>
                <div className="text-xs text-amber-800">The project manager will follow up shortly.</div>
              </div>
            </div>
          )}

          {/* OVERVIEW */}
          <div className="px-6 py-5 grid grid-cols-2 gap-4 border-b border-slate-100">
            <Stat icon={Calendar} label="Window" value={`${window_.start_date} → ${window_.end_date}`} />
            <Stat icon={Building2} label="Your phases" value={`${bars.length} bar${bars.length === 1 ? '' : 's'}`} />
          </div>

          {/* BARS LIST */}
          <div className="px-6 py-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Your schedule</div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <div className="col-span-5">Phase</div>
                <div className="col-span-2">Scope</div>
                <div className="col-span-2 text-right">Start</div>
                <div className="col-span-2 text-right">End</div>
                <div className="col-span-1 text-right">Days</div>
              </div>
              {bars.map((b, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50" data-testid={`cascade-bar-${i}`}>
                  <div className="col-span-5 text-sm font-medium text-slate-900 truncate">{b.phase_name}</div>
                  <div className="col-span-2 text-xs text-slate-600">{b.unit === 'all' ? 'All units' : `Unit ${b.unit}`}</div>
                  <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">{formatDate(b.start_date)}</div>
                  <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">{formatDate(b.end_date)}</div>
                  <div className="col-span-1 text-right text-sm font-bold text-slate-900">{b.duration_days}d</div>
                </div>
              ))}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          {!submitted && (
            <div className="px-6 py-5 border-t border-slate-100 bg-slate-50/60">
              {initialDecision !== 'push_back' && (
                <button
                  onClick={() => submit('confirm')}
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-xl shadow-sm mr-3 mb-3 sm:mb-0"
                  data-testid="cascade-confirm-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Confirm these dates
                </button>
              )}

              <div className={initialDecision === 'push_back' ? '' : 'mt-4'}>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-600 block mb-1">
                  Or push back / propose change <span className="text-slate-400">(optional note)</span>
                </label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. We need to push framing Bldg 2 a week later — our crew is on another job until then."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="cascade-pushback-note"
                />
                <button
                  onClick={() => submit('push_back')}
                  disabled={submitting}
                  className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-amber-50 disabled:opacity-60 text-amber-800 border border-amber-300 font-semibold rounded-xl"
                  data-testid="cascade-pushback-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  Send push-back to PM
                </button>
              </div>
            </div>
          )}

          {/* FOOTER */}
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center gap-2 text-[11px] text-slate-500">
            <Mail className="w-3 h-3" />
            Sent to {c.email}. Reply directly to this email if you have questions for the PM.
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-100">{children}</div>;
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
